import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { color, notifTokens } from "@isale/design-tokens";

/**
 * Snackbar (Notifications pattern 07 — web counterpart of mobile
 * `apps/isale-mobile/src/shared/ui/Snackbar.tsx`).
 *
 * Dark bottom-anchored card with an optional Undo action and a thin
 * timer bar that drains as the snackbar approaches auto-dismiss.
 *
 * Distinguished from Toast: snackbars are reserved for *destructive*
 * actions the user can still take back (e.g. "Đã xoá fact", undo 5s).
 * Toast is for non-undoable feedback ("Đã lưu").
 *
 * API:
 *
 *   const snack = useSnackbar();
 *   snack.show("Đã xoá fact", { onUndo: () => restore() });
 *
 * Mount once at the app root:
 *
 *   <SnackbarProvider>
 *     <App />
 *   </SnackbarProvider>
 */

interface SnackbarItem {
  id: number;
  message: string;
  onUndo?: () => void;
}

interface SnackbarApi {
  show: (message: string, opts?: { onUndo?: () => void }) => void;
  dismiss: (id: number) => void;
}

const SnackbarContext = createContext<SnackbarApi | null>(null);

let counter = 0;

export function SnackbarProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<SnackbarItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((arr) => arr.filter((s) => s.id !== id));
  }, []);

  const show = useCallback(
    (message: string, opts?: { onUndo?: () => void }) => {
      const id = ++counter;
      setItems((arr) => [...arr, { id, message, onUndo: opts?.onUndo }]);
      setTimeout(() => dismiss(id), notifTokens.duration.snackbar.hold);
    },
    [dismiss],
  );

  const api: SnackbarApi = { show, dismiss };

  return (
    <SnackbarContext.Provider value={api}>
      {children}
      <SnackbarStack items={items} onDismiss={dismiss} />
    </SnackbarContext.Provider>
  );
}

export function useSnackbar(): SnackbarApi {
  const ctx = useContext(SnackbarContext);
  if (!ctx) {
    throw new Error("useSnackbar() must be used inside <SnackbarProvider />.");
  }
  return ctx;
}

function SnackbarStack({
  items,
  onDismiss,
}: {
  items: SnackbarItem[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-6 flex flex-col items-center gap-2 px-4 sm:bottom-8"
      style={{ zIndex: notifTokens.zIndex.snackbar }}
    >
      {items.map((s) => (
        <SnackbarView key={s.id} item={s} onDismiss={() => onDismiss(s.id)} />
      ))}
    </div>
  );
}

function SnackbarView({
  item,
  onDismiss,
}: {
  item: SnackbarItem;
  onDismiss: () => void;
}) {
  // Animate the timer bar from 100% → 0% over the hold duration. Pure
  // CSS transition so we don't pull in framer-motion for one bar.
  const [progress, setProgress] = useState(100);
  useEffect(() => {
    const r = requestAnimationFrame(() => setProgress(0));
    return () => cancelAnimationFrame(r);
  }, []);

  return (
    <div
      role="alert"
      aria-live="polite"
      className="pointer-events-auto w-full max-w-md overflow-hidden text-white shadow-float"
      style={{
        backgroundColor: color.ink /* palette-ok: fixed near-black snackbar surface (Material-style, white text both modes) */,
        borderRadius: notifTokens.radius.toast,
        animation: `isale-snack-in ${notifTokens.duration.snackbar.in}ms cubic-bezier(0.32, 0.72, 0, 1)`,
      }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="flex-1 text-sm font-bold">{item.message}</span>
        {item.onUndo && (
          <button
            type="button"
            onClick={() => {
              item.onUndo?.();
              onDismiss();
            }}
            className="text-sm font-bold text-indigo-300 transition hover:text-indigo-200" /* palette-ok: light-indigo accent on the fixed near-black snackbar surface — no theme-aware token fits */
          >
            Hoàn tác
          </button>
        )}
      </div>
      {/* Drain bar: indigo, transitions over the snackbar hold duration. */}
      <div
        className="h-0.5 bg-primary-soft0 transition-[width] ease-linear"
        style={{
          width: `${progress}%`,
          transitionDuration: `${notifTokens.duration.snackbar.hold}ms`,
        }}
      />
      <style>{`
        @keyframes isale-snack-in {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
